import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Upload } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export function AdminGreetings() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [friendName, setFriendName] = useState("");
  const [publish, setPublish] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ caption: "", friend_name: "", is_published: true });
  const fileRef = useRef();

  const load = async () => { const { data } = await api.get("/admin/videos"); setItems(data); };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("friend_name", friendName);
      fd.append("is_published", publish);
      await api.post("/admin/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video pubblicato"); setCaption(""); setFriendName(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const saveEdit = async () => {
    try { await api.put(`/admin/videos/${editing}`, editForm); toast.success("Aggiornato"); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo video?")) return;
    await api.delete(`/admin/videos/${id}`); toast.success("Eliminato"); load();
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  return (
    <div>
      <h2 className="mb-4 font-display text-2xl font-extrabold uppercase text-zinc-950">Saluti di Tony</h2>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4">
        <Label>Nome amico salutato (facoltativo)</Label>
        <input data-testid="video-friend-input" value={friendName} onChange={(e) => setFriendName(e.target.value)} placeholder="Es. Marco" className="mt-1 mb-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-green-500" />
        <Label>Didascalia (testo)</Label>
        <Textarea data-testid="video-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Es. Un saluto a Marco e Giulia!" className="mt-1 rounded-xl" />
        <label className="mt-3 flex items-center justify-between rounded-2xl bg-slate-100 p-3">
          <span className="text-sm font-semibold text-zinc-700">Pubblica subito</span>
          <Switch data-testid="video-publish-switch" checked={publish} onCheckedChange={setPublish} />
        </label>
        <input ref={fileRef} type="file" accept="video/*" hidden onChange={upload} />
        <Button data-testid="video-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="mt-3 w-full rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
          {uploading ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Caricamento...</> : <><Upload className="mr-1 h-4 w-4" /> Carica e pubblica video</>}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.length === 0 && <p className="text-sm text-zinc-500">Nessun video caricato.</p>}
        {items.map((v) => (
          <div key={v.id} data-testid={`admin-video-${v.id}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <video src={fileUrl(v.video_path)} controls playsInline className="h-auto w-full bg-black" />
            <div className="p-3">
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${v.is_published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{v.is_published ? "Pubblico" : "Bozza"}</span>
                <div className="flex gap-1">
                  <Button onClick={() => { setEditing(v.id); setEditForm({ caption: v.caption || "", friend_name: v.friend_name || "", is_published: v.is_published }); }} size="sm" variant="ghost"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`admin-video-delete-${v.id}`} onClick={() => del(v.id)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="mt-2 text-sm text-zinc-700">{v.caption}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">Modifica video</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Nome amico salutato</Label>
            <input data-testid="video-edit-friend-input" value={editForm.friend_name} onChange={(e) => setEditForm({ ...editForm, friend_name: e.target.value })} placeholder="Es. Marco" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-green-500" />
            <Label>Didascalia</Label>
            <Textarea value={editForm.caption} onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })} className="rounded-xl" />
            <label className="flex items-center justify-between rounded-2xl bg-slate-100 p-3">
              <span className="text-sm font-semibold text-zinc-700">Pubblicato</span>
              <Switch checked={editForm.is_published} onCheckedChange={(x) => setEditForm({ ...editForm, is_published: x })} />
            </label>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
